import React, { useEffect, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, Copy, Download, AlertTriangle, KeyRound, CheckCircle2 } from "lucide-react";

/**
 * Panneau de gestion du 2FA (TOTP) pour l'utilisateur courant.
 * - Activation : QR + code à vérifier + 10 codes de récupération
 * - Désactivation : nécessite le mot de passe
 */
export default function TwoFASection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup state
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [enabling, setEnabling] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  // Disable state
  const [pwd, setPwd] = useState("");
  const [disabling, setDisabling] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) {
      toast.error("Impossible de charger le statut 2FA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const beginSetup = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setCode("");
      setBackupCodes(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setEnabling(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: code.trim() });
      setBackupCodes(data.backup_codes);
      setSetupData(null);
      setCode("");
      toast.success("2FA activé !");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code incorrect");
    } finally {
      setEnabling(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setDisabling(true);
    try {
      await api.post("/auth/2fa/disable", { password: pwd });
      toast.success("2FA désactivé");
      setPwd("");
      setBackupCodes(null);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setDisabling(false);
    }
  };

  const downloadBackup = () => {
    const text = `TPCO Sénégal — Codes de récupération 2FA\nGénérés le ${new Date().toLocaleString("fr-FR")}\n\n${backupCodes.join("\n")}\n\nConservez ces codes en lieu sûr. Chaque code n'est utilisable qu'une seule fois.\n`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tpco-2fa-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyBackup = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Codes copiés");
  };

  if (loading) {
    return (
      <div className="tpco-card p-6"><Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" /></div>
    );
  }

  return (
    <div className="tpco-card p-6" data-testid="twofa-section">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${status?.enabled ? "bg-[#E5F0E8] text-[#2D7A3F]" : "bg-[#F1F3FB] text-[#4B5775]"}`}>
            {status?.enabled ? <ShieldCheck className="w-6 h-6" /> : <ShieldOff className="w-6 h-6" />}
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-[#0F1A3D]">Authentification à 2 facteurs (2FA)</h3>
            <div className="text-xs text-[#4B5775] mt-1">
              {status?.enabled
                ? <>Activé · {status.backup_codes_remaining} code{status.backup_codes_remaining > 1 ? "s" : ""} de récupération restant{status.backup_codes_remaining > 1 ? "s" : ""}</>
                : "Désactivé. Renforcez la sécurité de votre compte en activant le 2FA."}
            </div>
          </div>
        </div>
        <div className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded ${status?.enabled ? "bg-[#E5F0E8] text-[#2D7A3F]" : "bg-[#FBEAEA] text-[#7A2D2D]"}`}>
          {status?.enabled ? "ACTIF" : "INACTIF"}
        </div>
      </div>

      {/* === ENABLE FLOW === */}
      {!status?.enabled && !setupData && !backupCodes && (
        <button
          type="button"
          onClick={beginSetup}
          className="h-10 px-4 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold uppercase tracking-wider"
          data-testid="twofa-setup-btn"
        >
          Activer le 2FA
        </button>
      )}

      {setupData && (
        <div className="border-t border-[#E2E5F1] pt-5 mt-5">
          <div className="bg-[#FFF4E5] border border-[#FFE4B0] rounded-md p-3 mb-4 flex items-start gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 shrink-0" />
            <div className="text-[#7A5A1A]">
              <strong>1.</strong> Installez <em>Google Authenticator</em>, <em>Authy</em> ou <em>1Password</em> sur votre téléphone.<br />
              <strong>2.</strong> Scannez le QR-code ci-dessous (ou copiez le secret).<br />
              <strong>3.</strong> Saisissez le code à 6 chiffres affiché par l'application.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="text-center">
              <div className="inline-block bg-white p-3 rounded-lg border border-[#E2E5F1]">
                <img src={setupData.qr_code} alt="QR code 2FA" className="w-48 h-48" data-testid="twofa-qr" />
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold">Secret manuel</div>
                <div className="font-mono text-xs bg-[#F8FAFC] border border-[#E2E5F1] rounded px-2 py-1.5 mt-1 select-all" data-testid="twofa-secret">{setupData.secret}</div>
              </div>
            </div>
            <form onSubmit={enable} className="space-y-3">
              <div>
                <label className="label-tiny block mb-1.5">Code à 6 chiffres</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123 456"
                  className="w-full h-12 px-3 text-lg font-mono tracking-widest text-center rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                  autoFocus
                  data-testid="twofa-verify-code"
                />
              </div>
              <button
                type="submit"
                disabled={enabling || code.length !== 6}
                className="w-full h-11 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="twofa-verify-btn"
              >
                {enabling ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Activer le 2FA
              </button>
              <button
                type="button"
                onClick={() => setSetupData(null)}
                className="w-full text-xs text-[#4B5775] hover:text-[#0F1A3D]"
              >Annuler</button>
            </form>
          </div>
        </div>
      )}

      {/* === SHOW BACKUP CODES (UNE FOIS APRÈS ACTIVATION) === */}
      {backupCodes && (
        <div className="border-t border-[#E2E5F1] pt-5 mt-5" data-testid="twofa-backup-codes">
          <div className="bg-[#E5F0E8] border border-[#B9D8C2] rounded-md p-3 mb-4 flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#2D7A3F] shrink-0 mt-0.5" />
            <div className="text-xs text-[#2D5A3C]">
              <div className="font-bold">2FA activé avec succès</div>
              <div className="mt-0.5">Sauvegardez ces 10 codes en lieu sûr. Ils permettent de vous connecter si vous perdez votre téléphone — chacun n'est utilisable qu'une seule fois.</div>
            </div>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E5F1] rounded-md p-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-sm font-bold text-[#0F1A3D]">
              {backupCodes.map((c, i) => <div key={i} className="bg-white border border-[#E2E5F1] rounded px-2 py-1.5 text-center select-all">{c}</div>)}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={downloadBackup} className="flex-1 h-9 rounded-md border border-[#1E3A8A] text-[#1E3A8A] text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Télécharger .txt
              </button>
              <button onClick={copyBackup} className="flex-1 h-9 rounded-md border border-[#E2E5F1] text-[#4B5775] hover:border-[#1E3A8A] hover:text-[#1E3A8A] text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copier
              </button>
            </div>
          </div>
          <button onClick={() => setBackupCodes(null)} className="text-xs text-[#4B5775] hover:text-[#0F1A3D] mt-3">J'ai sauvegardé ces codes, fermer</button>
        </div>
      )}

      {/* === DISABLE === */}
      {status?.enabled && !backupCodes && (
        <form onSubmit={disable} className="border-t border-[#E2E5F1] pt-5 mt-5">
          <div className="text-xs text-[#4B5775] mb-3 flex items-start gap-2">
            <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
            <div>Pour désactiver le 2FA, confirmez avec votre mot de passe.</div>
          </div>
          <div className="flex gap-2">
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
              placeholder="Mot de passe"
              className="flex-1 h-10 px-3 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
              data-testid="twofa-disable-pwd"
            />
            <button
              type="submit"
              disabled={disabling}
              className="h-10 px-4 rounded-md border-2 border-[#C83B3B] text-[#C83B3B] hover:bg-[#C83B3B] hover:text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="twofa-disable-btn"
            >
              {disabling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />}
              Désactiver
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
